import { portalRequest } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company
/**
 * ExportData.tsx - Export-only page for accounts pending deletion
 *
 * v2.91 - 2026-02-07 - Initial implementation for deletion lifecycle
 *
 * Shown when tenant_meta.status === 'pending_deletion'.
 * Allows users to:
 * - See deletion countdown
 * - Export their data (discovery, connections, settings, all)
 * - Cancel deletion (with re-auth warning)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../hooks/usePermissions'; // v4.28 - G10-B
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Download as DownloadIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

interface ExportDataProps {
  deletionScheduledAt: string | null;
}

const ExportData: React.FC<ExportDataProps> = ({ deletionScheduledAt }) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // v4.28 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): exports and cancel-deletion follow the server's permission map
  const { hasPermission } = usePermissions();
  const canExportAll = hasPermission('exportAllData');
  const canCancelDeletion = hasPermission('deleteAccount');

  const [exportLoading, setExportLoading] = useState<Record<string, boolean>>({});
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // v2.91 - Calculate days remaining
  const daysRemaining = deletionScheduledAt
    ? Math.max(0, Math.ceil((new Date(deletionScheduledAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 30;

  const deletionDate = deletionScheduledAt
    ? new Date(deletionScheduledAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Unknown';

  // v2.91 - Export handler
  const handleExport = async (type: 'discovery' | 'connections' | 'settings' | 'all') => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    setExportError(null);
    setExportSuccess(null);

    try {
      const token = await getToken();
      const response = await portalRequest(`/api/export/${type}`, {
        headers: token ? { 'X-Authorization': `Bearer ${token}` } : {},
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Create blob download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mylojic-export-${type}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setExportSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} data exported successfully.`);
    } catch (err: any) {
      setExportError(err.message || 'Export failed. Please try again.');
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // v2.91 - Cancel deletion handler
  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    setExportError(null);

    try {
      const token = await getToken();
      const response = await portalRequest('/api/settings/account/cancel-deletion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'X-Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to cancel deletion');
      }

      const result = await response.json();

      setCancelDialogOpen(false);

      if (result.connectionsNeedReauth) {
        // Redirect with reauth notice
        navigate('/dashboard/data?reactivated=true');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setExportError(err.message || 'Failed to cancel deletion. Please try again.');
      setCancelLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        {/* Warning Banner */}
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Account Scheduled for Deletion
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Your account will be permanently deleted on {deletionDate} ({daysRemaining} days remaining).
            Download your data below before it is removed.
          </Typography>
        </Alert>

        {/* Success/Error alerts */}
        {exportSuccess && (
          <Alert severity="success" onClose={() => setExportSuccess(null)} sx={{ mb: 2 }}>
            {exportSuccess}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" onClose={() => setExportError(null)} sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}

        {/* Export Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Your Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Download your data as JSON files. No sensitive credentials (API keys, OAuth tokens) are included.
          </Typography>

          {!canExportAll ? (
            <Alert severity="info">{t('teamAccess.noExportPermission')}</Alert>
          ) : (
          <Stack spacing={2}>
            <Button
              variant="outlined"
              startIcon={exportLoading.discovery ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={() => handleExport('discovery')}
              disabled={exportLoading.discovery}
              fullWidth
            >
              Export Discovery Answers
            </Button>
            <Button
              variant="outlined"
              startIcon={exportLoading.connections ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={() => handleExport('connections')}
              disabled={exportLoading.connections}
              fullWidth
            >
              Export Connections List
            </Button>
            <Button
              variant="outlined"
              startIcon={exportLoading.settings ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={() => handleExport('settings')}
              disabled={exportLoading.settings}
              fullWidth
            >
              Export Settings &amp; Team
            </Button>

            <Divider />

            <Button
              variant="contained"
              startIcon={exportLoading.all ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={() => handleExport('all')}
              disabled={exportLoading.all}
              fullWidth
            >
              Export Everything
            </Button>
          </Stack>
          )}
        </Paper>

        {/* Cancel Deletion Section */}
        {canCancelDeletion && (
        <Paper sx={{ p: 3, border: '1px solid', borderColor: 'success.main' }}>
          <Typography variant="h6" gutterBottom>
            Changed Your Mind?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You can cancel the deletion and reactivate your account at any time before {deletionDate}.
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<RestoreIcon />}
            onClick={() => setCancelDialogOpen(true)}
          >
            Cancel Deletion &amp; Reactivate
          </Button>
        </Paper>
        )}

        {/* Cancel Deletion Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => !cancelLoading && setCancelDialogOpen(false)}>
          <DialogTitle>Reactivate Your Account</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Your account will be reactivated immediately. However, please note:
            </DialogContentText>
            <Alert severity="info" sx={{ mt: 2 }}>
              Your data source connections (QuickBooks, Xero, etc.) were disconnected when deletion was requested.
              You will need to re-authorize them from the Data Sources page after reactivation.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelLoading}>
              Go Back
            </Button>
            <Button
              onClick={handleCancelDeletion}
              variant="contained"
              color="success"
              disabled={cancelLoading}
              startIcon={cancelLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              {cancelLoading ? 'Reactivating...' : 'Yes, Reactivate My Account'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ExportData;
